import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CARD_BG = '#0D0D1A';
const CYAN = '#00FFE5';
const PURPLE = '#7B2FFF';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const GOLD = '#FFD700';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const BenefitsShowcase = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "SARAH CHEN",
      role: "Digital Operator",
      content: "RecallCore transformed my execution process. The AI agents handled everything — I just approved and moved.",
      rating: 5,
      achievement: "6-figure business in 8 months"
    },
    {
      id: 2,
      name: "MARCUS RODRIGUEZ",
      role: "Entrepreneur",
      content: "The mission execution kept me accountable. I\'ve never shipped faster or with more clarity.",
      rating: 5,
      achievement: "Launched 3 successful products"
    },
    {
      id: 3,
      name: "EMILY WATSON",
      role: "Operator",
      content: "82+ specialized agents is like having an entire team. It\'s revolutionized how I execute.",
      rating: 5,
      achievement: "Scaled operations 300%"
    }
  ];

  const platformBenefits = [
    { icon: 'Bot', title: '82+ AI AGENTS', description: 'Specialized agents for every execution need', color: CYAN },
    { icon: 'TrendingUp', title: 'MISSION TRACKING', description: 'Full run history and audit trails', color: CYAN },
    { icon: 'Zap', title: 'AUTONOMOUS EXECUTION', description: 'No micromanaging. No follow-up prompts.', color: PURPLE },
    { icon: 'Shield', title: 'APPROVAL-FIRST', description: 'Nothing runs without your explicit sign-off', color: CYAN },
    { icon: 'FileText', title: 'RESULTS DELIVERED', description: 'Action cards. Clear outputs. Work finished.', color: GOLD },
    { icon: 'Lock', title: 'PRIVATE & SECURE', description: 'Your data never shared or trained on', color: CYAN },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials?.length]);

  return (
    <div className="space-y-6">
      {/* Testimonials */}
      <div style={{ background: CARD_BG, border: '1px solid rgba(0,255,229,0.2)', padding: '1.5rem' }}>
        <div className="text-center mb-6">
          <h3 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '0.9rem', color: ICE, marginBottom: '0.25rem', letterSpacing: '0.1em' }}>
            OPERATOR REPORTS
          </h3>
          <p style={{ color: MUTED, fontSize: '0.7rem', fontFamily: MONO }}>Field intelligence from active users</p>
        </div>

        <div className="relative min-h-[180px]">
          {testimonials?.map((testimonial, index) => (
            <div key={testimonial?.id} className="absolute inset-0 transition-all duration-500"
              style={{ opacity: index === currentTestimonial ? 1 : 0, transform: index === currentTestimonial ? 'translateX(0)' : 'translateX(16px)' }}>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} style={{ color: i < testimonial?.rating ? GOLD : 'rgba(136,153,170,0.3)', fontSize: '0.8rem' }}>★</span>
                  ))}
                </div>
                <blockquote style={{ color: ICE, fontSize: '0.82rem', lineHeight: 1.7, fontFamily: MONO, marginBottom: '1rem' }}>
                  &gt; "{testimonial?.content}"
                </blockquote>
                <div>
                  <p style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '0.75rem', color: CYAN }}>{testimonial?.name}</p>
                  <p style={{ color: MUTED, fontSize: '0.65rem', fontFamily: MONO, marginBottom: '0.5rem' }}>{testimonial?.role}</p>
                  <div className="inline-flex items-center gap-1 px-2 py-1" style={{ background: 'rgba(0,255,229,0.08)', border: '1px solid rgba(0,255,229,0.2)' }}>
                    <Icon name="Trophy" size={10} style={{ color: GOLD }} />
                    <span style={{ fontSize: '0.65rem', fontFamily: MONO, color: GOLD }}>{testimonial?.achievement}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center space-x-2 mt-6">
          {testimonials?.map((_, index) => (
            <button key={index} onClick={() => setCurrentTestimonial(index)}
              style={{ width: index === currentTestimonial ? '20px' : '6px', height: '3px', background: index === currentTestimonial ? CYAN : 'rgba(0,255,229,0.3)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }} />
          ))}
        </div>
      </div>
      {/* Platform Benefits */}
      <div style={{ background: CARD_BG, border: '1px solid rgba(0,255,229,0.2)', padding: '1.5rem' }}>
        <h3 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '0.85rem', color: ICE, marginBottom: '1.25rem', textAlign: 'center', letterSpacing: '0.1em' }}>
          SYSTEM CAPABILITIES
        </h3>
        <div className="grid grid-cols-1 gap-3">
          {platformBenefits?.map((benefit, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 transition-all"
              style={{ border: '1px solid rgba(0,255,229,0.08)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,255,229,0.3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,255,229,0.08)'}
            >
              <div className="p-1.5 flex-shrink-0" style={{ background: 'rgba(0,255,229,0.08)' }}>
                <Icon name={benefit?.icon} size={16} style={{ color: benefit?.color }} />
              </div>
              <div>
                <h4 style={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.72rem', color: ICE, marginBottom: '2px', letterSpacing: '0.08em' }}>{benefit?.title}</h4>
                <p style={{ fontSize: '0.68rem', color: MUTED, fontFamily: MONO, lineHeight: 1.5 }}>{benefit?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Trust Indicators */}
      <div style={{ background: CARD_BG, border: '1px solid rgba(0,255,229,0.2)', padding: '1.5rem' }}>
        <div className="text-center">
          <h4 style={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.7rem', color: CYAN, marginBottom: '1rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            SYSTEM STATUS
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: 'Shield', label: 'SSL SECURED', sub: 'Bank-level encryption' },
              { icon: 'Award', label: 'AI CERTIFIED', sub: 'Industry standard' },
            ]?.map(item => (
              <div key={item?.label} className="p-3" style={{ background: 'rgba(0,255,229,0.04)', border: '1px solid rgba(0,255,229,0.12)' }}>
                <Icon name={item?.icon} size={18} style={{ color: CYAN, margin: '0 auto 6px', display: 'block' }} />
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: ICE, fontFamily: MONO, textAlign: 'center' }}>{item?.label}</p>
                <p style={{ fontSize: '0.6rem', color: MUTED, fontFamily: MONO, textAlign: 'center' }}>{item?.sub}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(0,255,229,0.15)' }}>
            <p style={{ fontSize: '0.65rem', color: MUTED, fontFamily: MONO }}>30-day money-back guarantee · Cancel anytime</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsShowcase;